import { sessionlessEvidenceAwarenessDevVariantOverrideAtom } from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { GET_EVIDENCE_AWARENESS_POPUP_VARIANT } from '@/components/constants/flags';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { isModalClosed } from '@/lib/modalClosedRules';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';

const NEW_USER_ROUTES_TO_SKIP = new Set<string>([
  '/scribe/new-user',
  '/new-team-user',
  '/organization-access-required',
  '/account-suspended',
]);

/**
 * Gates the sessionless Evidence awareness popover (not modal-queue): auth, route, variant
 * flag, Evidence access, and `modalClosedRules` cooldowns. Brand-new users get a passive
 * anchor in `useSessionlessEvidenceAwarenessPopover` before the first visible impression;
 * after that, eligibility tracks cadence (4-day) and long suppression (3 dismisses / 60-day).
 */
export function useSessionlessEvidenceAwarenessEligibility(): boolean {
  const router = useRouter();
  const token = useAuthToken();
  const { data: user, isSuccess, isPending: isUserPending } = useQueryUser();
  const { data: organisationInfo } = useQueryUser((u) => u?.organisation_info);
  const { isPending: isKindePending } = useQueryKindeProperties();
  const { hasEvidenceAccess } = useEvidenceAccess();
  const devVariantOverride = useAtomValue(
    sessionlessEvidenceAwarenessDevVariantOverrideAtom
  );

  const isNonProd = getEnvironment() !== ENV_NAME.PROD;
  const variant =
    isNonProd && devVariantOverride != null
      ? devVariantOverride
      : GET_EVIDENCE_AWARENESS_POPUP_VARIANT();

  const isAuthLoaded =
    !!token &&
    isSuccess &&
    !isUserPending &&
    !isKindePending &&
    !!user &&
    !!organisationInfo &&
    !isEmpty(organisationInfo);
  const isOnAllowedRoute = !NEW_USER_ROUTES_TO_SKIP.has(router.pathname);

  const arePreconditionsMet =
    isAuthLoaded && isOnAllowedRoute && variant !== 'control' && hasEvidenceAccess;

  if (!arePreconditionsMet) return false;

  const modalView = user?.seen_modals?.[ModalId.SESSIONLESS_EVIDENCE_AWARENESS];
  return !isModalClosed(ModalId.SESSIONLESS_EVIDENCE_AWARENESS, modalView);
}
